import React from 'react';
import * as Styled from './OBannerFeature.styles';
import { OBannerFeatureProps, OBannerFeatureLayout, OBannerFeatureContentAlignment } from './OBannerFeature.types';
import { AspectRatio, MediaQuery, ResponsiveImageSpec } from '../../utils/ImageUtils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  ABodyColor,
  ABodySize,
  AEyebrow,
  AEyebrowVariant,
  AHeader,
  AHeaderSize,
  AHeaderTag,
  MButton,
  MButtonSize,
  MButtonType,
  MMediaSelector,
  MMediaSelectorMediaType,
  MMediaSelectorProps,
  MParagraph,
  MParagraphAlignment,
} from '@marriott/mi-web-ui-kit';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OBannerFeature';
const TEST_ID = 'mi-web-ui-kit-OBannerFeature';

const RESPONSIVE_IMAGE_SPECS: ResponsiveImageSpec[] = [
  {
    media: MediaQuery.MAX_WIDTH_SM,
    aspectRatio: AspectRatio.CLASSIC_HOR,
    imgWidthPx: 540,
  },
  {
    media: MediaQuery.MIN_WIDTH_MD,
    aspectRatio: AspectRatio.SQUARE,
    imgWidthPx: 360,
  },
  {
    media: MediaQuery.MIN_WIDTH_LG,
    aspectRatio: AspectRatio.CLASSIC_HOR,
    imgWidthPx: 560,
  },
  {
    media: MediaQuery.MIN_WIDTH_XL,
    aspectRatio: AspectRatio.CLASSIC_HOR,
    imgWidthPx: 665,
  },
];

export const OBannerFeature = ({
  contentAlignment = OBannerFeatureContentAlignment.Left,
  layout = OBannerFeatureLayout.Default,
  aEyebrow,
  aHeader,
  mParagraph,
  mTextBlockSimple,
  mButton,
  mButton2,
  aImage,
  mMediaSelector,
  trackingProperties,
}: OBannerFeatureProps) => {
  // Resolve text fields new-prop-first, falling back to the deprecated
  // `mTextBlockSimple` so legacy consumers keep rendering without change.
  const eyebrowText = aEyebrow?.eyebrowText ?? mTextBlockSimple?.aEyebrow?.eyebrowText;
  const headerText = aHeader?.text ?? mTextBlockSimple?.aHeader?.text;
  const headerSize = (aHeader?.size ?? mTextBlockSimple?.aHeader?.size) as AHeaderSize | undefined;
  const headerTag = (aHeader?.headerTag ?? mTextBlockSimple?.aHeader?.headerTag) as AHeaderTag | undefined;

  const bodyText = mParagraph?.aBody?.bodyText ?? mTextBlockSimple?.aBody?.bodyText;
  const bodySize = (mParagraph?.aBody?.size ?? mTextBlockSimple?.aBody?.size) as ABodySize | undefined;
  const bodyColor = (mParagraph?.aBody?.color ?? mTextBlockSimple?.aBody?.color) as ABodyColor | undefined;

  // Truncation is on unless the author explicitly disables it
  const isTruncated = mParagraph?.truncated !== false && String(mParagraph?.truncated).toLowerCase() !== 'false';

  const isCentered = contentAlignment === OBannerFeatureContentAlignment.Center;
  const paragraphAlignment = isCentered ? MParagraphAlignment.Centered : MParagraphAlignment.Left;

  // Prefer the explicit media selector. When absent, map the deprecated
  // `aImage` into an image media slot so existing content keeps working. The
  // organism owns the responsive aspect ratios, so inject its specs into every
  // image-bearing slot rather than inheriting MMediaImage's defaults.
  const baseMediaSelector: MMediaSelectorProps | undefined =
    mMediaSelector ??
    (aImage?.image
      ? { mediaType: MMediaSelectorMediaType.Image, mMediaImage: { aImage: { image: aImage.image } } }
      : undefined);

  const resolvedMediaSelector: MMediaSelectorProps | undefined = baseMediaSelector && {
    ...baseMediaSelector,
    mMediaImage: { ...baseMediaSelector.mMediaImage, renditionSpecs: RESPONSIVE_IMAGE_SPECS },
    ...(baseMediaSelector.mMediaCarousel && {
      mMediaCarousel: { ...baseMediaSelector.mMediaCarousel, renditionSpecs: RESPONSIVE_IMAGE_SPECS },
    }),
  };

  const renderButton = (button: NonNullable<OBannerFeatureProps['mButton']>) =>
    button.ctaLink && button.buttonText ? (
      <MButton
        buttonText={button.buttonText}
        buttonType={button.buttonType as MButtonType}
        size={MButtonSize.M}
        ctaLink={button.ctaLink}
        openInANewTab={button.openInANewTab === 'true'}
        trackingProperties={trackingProperties}
      />
    ) : null;

  const primaryCta = mButton ? renderButton(mButton) : null;
  const secondaryCta = mButton2 ? renderButton(mButton2) : null;

  return (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      $layout={layout}
      className="container d-flex justify-content-center align-items-center"
    >
      {(layout === OBannerFeatureLayout.Default || layout === OBannerFeatureLayout.Reverse) && (
        <>
          <Styled.ContentSection $contentAlignment={contentAlignment}>
            <Styled.TextBlockWrapper $alignment={contentAlignment}>
              {eyebrowText && <AEyebrow variant={AEyebrowVariant.Primary}>{eyebrowText}</AEyebrow>}
              {headerText && (
                <AHeader
                  size={headerSize ?? AHeaderSize.TitleM}
                  text={headerText}
                  {...(headerTag ? { element: headerTag } : {})}
                />
              )}
              {bodyText && (
                <MParagraph
                  alignment={paragraphAlignment}
                  truncated={isTruncated}
                  truncateLines={11}
                  aBody={{
                    bodyText,
                    ...(bodySize ? { size: bodySize } : {}),
                    ...(bodyColor ? { color: bodyColor } : {}),
                  }}
                />
              )}
              {(primaryCta || secondaryCta) && (
                <Styled.ButtonWrapper $contentAlignment={contentAlignment}>
                  {primaryCta}
                  {secondaryCta}
                </Styled.ButtonWrapper>
              )}
            </Styled.TextBlockWrapper>
          </Styled.ContentSection>
          {resolvedMediaSelector && (
            <Styled.ImageSection className="col-12 col-md-6 col-lg-7 px-0">
              <MMediaSelector {...resolvedMediaSelector} className="feature-media" />
            </Styled.ImageSection>
          )}
        </>
      )}
    </Styled.Wrapper>
  );
};

export { OBannerFeatureContentAlignment };
export type { OBannerFeatureProps, OBannerFeatureLayout };
